import {DataStatus, Form, FormField} from 'platform/components';
import {VStack, Show} from 'platform/foundation';

import {useGetManufacturersQuery} from '@dms/api/core';
import {useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {FormToDataGridConnectorProps} from 'features/eag-grid';

import {useGetSupplierCustomersQuery} from '../../hooks/useGetSupplierCustomersQuery';
import {getOptionsFromCustomersAsSupplier} from '../../utils/getOptionsFromCustomersAsSupplier';
import {getOptionsFromManufacturers} from '../../utils/getOptionsFromManufacturers';
import {getOptionsFromWarehouses} from '../../utils/getOptionsFromWarehouses';

type MaterialArticleTabExternalFiltersForm = Partial<{
  warehouseId: string[];
  isAvailable: boolean;
  supplierId: string;
  isInStock: boolean;
  catalogNumber: string;
  manufacturerId: string;
}>;

type WarehouseTabExternalFiltersProps = {
  directSaleVariantId?: string;
  hideWarehouseField?: boolean;
} & FormToDataGridConnectorProps &
  RequiredTestIdProps;

export function WarehouseTabExternalFilters(props: WarehouseTabExternalFiltersProps) {
  const {
    data: warehouses,
    isLoading: isWarehousesLoading,
    isError: isWarehousesError,
  } = useGetWarehousesQuery({
    directSaleVariantId: props.directSaleVariantId,
  });

  const {
    data: manufacturers,
    isLoading: isManufacturersLoading,
    isError: isManufacturersError,
  } = useGetManufacturersQuery();

  const {
    data: suppliers,
    isLoading: isSuppliersLoading,
    isError: isSuppliersError,
  } = useGetSupplierCustomersQuery();

  const isLoading = isWarehousesLoading || isManufacturersLoading || isSuppliersLoading;
  const isError = isWarehousesError || isManufacturersError || isSuppliersError;

  const formValues: MaterialArticleTabExternalFiltersForm = {
    isAvailable: false,
    isInStock: false,
    warehouseId: [],
    ...props.values,
  };

  return (
    <DataStatus isLoading={isLoading} isError={isError}>
      <Form<MaterialArticleTabExternalFiltersForm>
        values={formValues}
        defaultValues={{}}
        onChange={props.onChange}
      >
        {(control) => (
          <VStack spacing={4}>
            <FormField
              control={control}
              name="catalogNumber"
              type="text"
              label={i18n.t('entity.warehouse.labels.catalogueNumber')}
              data-testid={suffixTestId('inputs.catalogueNumber', props)}
            />
            <Show when={!props.hideWarehouseField}>
              <FormField
                control={control}
                name="warehouseId"
                type="multiChoice"
                label={i18n.t('entity.warehouse.labels.warehouse')}
                options={getOptionsFromWarehouses(warehouses)}
                data-testid={suffixTestId('warehouse', props)}
              />
            </Show>
            <FormField
              control={control}
              name="manufacturerId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.manufacturer')}
              options={getOptionsFromManufacturers(manufacturers)}
              data-testid={suffixTestId('inputs.manufacturer', props)}
            />
            <FormField
              control={control}
              name="supplierId"
              type="multiChoice"
              label={i18n.t('entity.warehouse.labels.articleDefaultSupplier')}
              options={getOptionsFromCustomersAsSupplier(suppliers)}
              data-testid={suffixTestId('inputs.supplierId', props)}
            />
            <FormField
              control={control}
              name="isAvailable"
              label={i18n.t('entity.warehouse.labels.availableOnly')}
              type="checkbox"
              data-testid={suffixTestId('availableOnly', props)}
            />
            <FormField
              control={control}
              name="isInStock"
              label={i18n.t('entity.warehouse.labels.inStock')}
              type="checkbox"
              data-testid={suffixTestId('inStock', props)}
            />
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
